import API_BASE from './api.js';

const INSTALL_FLAG = '__ifAuthFetchInstalled';

function isApiRequest(url, apiOrigin) {
  return url.origin === apiOrigin && url.pathname.startsWith('/api/');
}

// These must never trigger a refresh-and-retry themselves, or a genuine
// bad-credentials/expired-refresh-token 401 would loop back into itself.
function isAuthRefreshExempt(url) {
  return url.pathname === '/api/auth/refresh' || url.pathname === '/api/auth/login';
}

// Everything AuthContext writes, cleared together once the session is over.
const AUTH_STORAGE_KEYS = [
  'accessToken',
  'idToken',
  'refreshToken',
  'expiresAt',
  'onboardingStatus',
  'plan',
  'userId',
  'ownUserId',
  'userEmail',
];

// Pages that render without a staff session. Bouncing a viewer off one of these
// would be worse than the expired session itself — public share links and the
// client portal have nothing to do with staff auth.
function isPublicPath(pathname) {
  return (
    pathname === '/' ||
    pathname.startsWith('/public/') ||
    pathname.startsWith('/client/') ||
    ['/signup', '/forgot-password', '/reset-password', '/confirm', '/pricing', '/privacy', '/terms'].includes(
      pathname
    )
  );
}

export default function installAuthFetch() {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  if (window[INSTALL_FLAG]) return;

  const nativeFetch = window.fetch.bind(window);
  const apiOrigin = new URL(API_BASE, window.location.origin).origin;

  // Single-flight so concurrent 401s (several calls firing right as the
  // token expires) trigger only one refresh instead of a burst of them.
  let refreshPromise = null;
  function refreshIdToken() {
    if (!refreshPromise) {
      refreshPromise = (async () => {
        try {
          const username = localStorage.getItem('userEmail');
          const refreshToken = localStorage.getItem('refreshToken');
          const idToken = localStorage.getItem('idToken');
          const res = await nativeFetch(`${API_BASE}/api/auth/refresh`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              ...(username ? { username } : {}),
              // The API lives on a different registrable domain than the app, so
              // the httpOnly cookies are third-party — Safari blocks them and
              // Firefox partitions them. Send the copies we already hold instead
              // of relying on the cookies arriving. The (expired) id token is how
              // the server finds the Cognito username the refresh must be signed
              // with.
              ...(refreshToken ? { refreshToken } : {}),
              ...(idToken ? { idToken } : {}),
            }),
          });
          // 401 here means Cognito rejected the refresh token itself — the
          // session is genuinely over. Any other failure (503, network, 4xx from
          // a malformed request) is transient: report no token, but do not sign
          // the user out over it.
          if (res.status === 401) return { idToken: null, sessionExpired: true };
          if (!res.ok) return { idToken: null, sessionExpired: false };

          const data = await res.json();
          if (!data?.ok || !data.accessToken || !data.idToken) {
            return { idToken: null, sessionExpired: false };
          }

          localStorage.setItem('accessToken', data.accessToken);
          localStorage.setItem('idToken', data.idToken);
          if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
          if (data.expiresIn) {
            localStorage.setItem('expiresAt', String(Date.now() + data.expiresIn * 1000));
          }

          return { idToken: data.idToken, sessionExpired: false };
        } catch {
          // Offline or DNS failure — transient by definition.
          return { idToken: null, sessionExpired: false };
        } finally {
          refreshPromise = null;
        }
      })();
    }
    return refreshPromise;
  }

  // A dead refresh token surfaces on every in-flight request at once, so only
  // the first caller gets to navigate.
  let redirectingToLogin = false;
  function handleSessionExpired() {
    if (redirectingToLogin) return;
    redirectingToLogin = true;

    AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));

    // Login is rendered at "/" by RootEntry — the same destination PrivateRoute
    // uses for unauthenticated users.
    if (!isPublicPath(window.location.pathname)) {
      window.location.assign('/');
    }
  }

  // Many callers (e.g. the client portal, which authenticates with its own
  // token) set Authorization explicitly — that must be left alone on the
  // first attempt. Only on a retry, once we already know this is an internal
  // admin/designer session (see the refreshToken check below), do we force
  // the freshly-refreshed idToken in.
  function buildHeaders(input, init, { forceIdToken = false } = {}) {
    const headers = new Headers(input instanceof Request ? input.headers : undefined);
    if (init?.headers) {
      new Headers(init.headers).forEach((value, key) => headers.set(key, value));
    }

    const idToken = localStorage.getItem('idToken');
    if (idToken && (forceIdToken || !headers.has('Authorization'))) {
      headers.set('Authorization', `Bearer ${idToken}`);
    }
    return headers;
  }

  window.fetch = async (input, init = undefined) => {
    let parsedUrl = null;

    try {
      const rawUrl =
        typeof input === 'string'
          ? input
          : input instanceof URL
            ? input.toString()
            : input?.url || '';
      parsedUrl = rawUrl ? new URL(rawUrl, window.location.origin) : null;
    } catch {
      parsedUrl = null;
    }

    if (!parsedUrl || !isApiRequest(parsedUrl, apiOrigin)) {
      return nativeFetch(input, init);
    }

    const requestInit = {
      ...init,
      credentials: init?.credentials ?? 'include',
      headers: buildHeaders(input, init),
    };

    const response = await nativeFetch(input, requestInit);

    const canRetry =
      response.status === 401 &&
      !isAuthRefreshExempt(parsedUrl) &&
      localStorage.getItem('refreshToken');

    if (!canRetry) {
      return response;
    }

    const { idToken: newIdToken, sessionExpired } = await refreshIdToken();

    if (sessionExpired) {
      handleSessionExpired();
      return response;
    }

    if (!newIdToken) {
      return response;
    }

    return nativeFetch(input, {
      ...init,
      credentials: init?.credentials ?? 'include',
      headers: buildHeaders(input, init, { forceIdToken: true }),
    });
  };

  window[INSTALL_FLAG] = true;
}
